<template>
  <div>
    <PTransparentLoader :loading="loading">
    <CRow>
      <CCol lg="12">
        <CCard class="no-borders">
          <CCardHeader>
            <div class="d-flex justify-content-between align-items-center">
              <h1 class="main-header mb-0">
                <translate translate-context="admin-certification">Certifications</translate>
              </h1>
            </div>
          </CCardHeader>
          <CCardBody class="px-0">
            <AjaxTable
              ref="ajaxTable"
              :fields="fields"
              :data="certifications"
              :fetch="fetchListHandler"
            >
              <!-- ID clickable -->
              <template #id="{item}">
                <td>
                  <CLink
                    v-if="canViewDetail"
                    :to="{name: 'adminCertificationDetail', params: {id: item.id}}"
                    class="font-weight-bold"
                  >
                    #{{ item.id }}
                  </CLink>
                  <span v-else class="font-weight-bold">#{{ item.id }}</span>
                </td>
              </template>

              <!-- Status filter -->
              <template #status-filter>
                <CSelect
                  :value.sync="statusFilterValue"
                  @update:value="onStatusFilterChange"
                  :options="statusOptions"
                  size="sm"
                />
              </template>

              <template #status="{item}">
                <td>
                  <CBadge :color="getStatusColor(item.status)" class="badge-outline">
                    <div class="d-flex align-items-center">
                      <CIcon :name="getStatusIcon(item.status)" size="custom-size" :height="8" class="mr-2"/>
                      <span class="font-weight-normal">{{ getStatusLabel(item.status) }}</span>
                    </div>
                  </CBadge>
                </td>
              </template>

              <template #contract_manager="{item}">
                <td>
                  <div>
                    <div class="font-weight-bold">{{ item.contract_manager_name }}</div>
                    <div class="small text-muted" v-if="item.contract_manager_email">
                      {{ item.contract_manager_email }}
                    </div>
                  </div>
                </td>
              </template>

              <template #region="{item}">
                <td>
                  <CBadge :color="item.region === 'eu' ? 'info' : 'secondary'">
                    {{ item.region === 'eu' ? 'EU' : 'Extra EU' }}
                  </CBadge>
                </td>
              </template>

              <template #vat_number="{item}">
                <td>
                  <code v-if="item.vat_number">{{ item.vat_number }}</code>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>

              <template #document="{item}">
                <td>
                  <template v-if="item.document">
                    <CLink :href="item.document" target="_blank" class="d-flex align-items-center">
                      <CIcon name="cipFile" size="sm" class="mr-1"/>
                      PDF
                    </CLink>
                  </template>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>

              <template #submitted_at="{item}">
                <td>
                  <span v-if="item.submitted_at">{{ item.submitted_at | formatDateTime }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>

              <template #actions="{item}">
                <td>
                  <div class="d-flex align-items-center">
                    <CButton
                      v-if="canApprove && item.status === CERTIFICATION_STATUS.PENDING"
                      color="success"
                      size="sm"
                      variant="outline"
                      @click="handleApprove(item)"
                      class="mr-2"
                    >
                      <CIcon name="cipCheck" size="sm" class="mr-1"/>
                      <translate translate-context="admin-certification">Approve</translate>
                    </CButton>

                    <CButton
                      v-if="canReject && (item.status === CERTIFICATION_STATUS.PENDING || item.status === CERTIFICATION_STATUS.CERTIFIED)"
                      color="danger"
                      size="sm"
                      variant="outline"
                      @click="openRejectModal(item)"
                    >
                      <CIcon name="cipClose" size="sm" class="mr-1"/>
                      <translate translate-context="admin-certification">Reject</translate>
                    </CButton>
                  </div>
                </td>
              </template>
            </AjaxTable>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
    </PTransparentLoader>

    <!-- Reject Modal -->
    <CModal
      :show.sync="showRejectModal"
      size="sm"
    >
      <template #header>
        <h5 class="modal-title">
          <translate translate-context="admin-certification">Reject Certification</translate>
        </h5>
        <CButtonClose @click="showRejectModal = false" />
      </template>
      <template #default>
        <div class="form-group">
          <label class="font-weight-bold">
            <translate translate-context="admin-certification">Reason</translate>
          </label>
          <CTextarea
            v-model="rejectReason"
            :placeholder="$pgettext('admin-certification', 'Enter the reason for rejection...')"
            rows="3"
          />
        </div>
      </template>
      <template #footer>
        <CButton color="secondary" variant="outline" @click="showRejectModal = false">
          <translate translate-context="admin-certification">Cancel</translate>
        </CButton>
        <CButton
          color="danger"
          :disabled="!rejectReason.trim()"
          @click="handleReject"
        >
          <translate translate-context="admin-certification">Reject</translate>
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AjaxTable from '@/domain/core/components/AjaxTable.vue'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import {
    CERTIFICATION_STATUS,
    CERTIFICATION_STATUS_LABELS,
    CERTIFICATION_STATUS_COLORS,
    CERTIFICATION_STATUS_ICONS,
    getStatusSelectOptions
} from './constants'

export default {
    name: 'AdminCertificationList',
    components: {
        AjaxTable,
        PTransparentLoader
    },
    data() {
        return {
            loading: false,
            statusFilterValue: CERTIFICATION_STATUS.PENDING,
            CERTIFICATION_STATUS,
            showRejectModal: false,
            rejectReason: '',
            rejectingItem: null,
            fields: [
                {
                    key: 'id',
                    label: this.$pgettext('admin-certification', 'ID'),
                    _style: 'width: 70px',
                    sorter: true,
                },
                {
                    key: 'status',
                    label: this.$pgettext('admin-certification', 'Status'),
                    _style: 'width: 130px',
                    filter: true,
                },
                {
                    key: 'contract_manager',
                    label: this.$pgettext('admin-certification', 'Contract Manager'),
                    _style: 'width: 200px',
                },
                {
                    key: 'region',
                    label: this.$pgettext('admin-certification', 'Region'),
                    _style: 'width: 100px',
                },
                {
                    key: 'vat_number',
                    label: this.$pgettext('admin-certification', 'VAT Number'),
                    _style: 'width: 150px',
                },
                {
                    key: 'document',
                    label: this.$pgettext('admin-certification', 'Document'),
                    _style: 'width: 100px',
                },
                {
                    key: 'submitted_at',
                    label: this.$pgettext('admin-certification', 'Submitted'),
                    _style: 'width: 150px',
                    sorter: true,
                },
                {
                    key: 'actions',
                    label: this.$pgettext('admin-certification', 'Actions'),
                    _style: 'width: 180px',
                }
            ]
        }
    },
    computed: {
        ...mapGetters('adminCertification', ['certifications']),
        ...mapGetters('login', ['hasAnyPerms']),
        canViewDetail() {
            return this.hasAnyPerms(['admin.certifications.detail'])
        },
        canApprove() {
            return this.hasAnyPerms(['admin.certifications.approve'])
        },
        canReject() {
            return this.hasAnyPerms(['admin.certifications.reject'])
        },
        statusOptions() {
            return getStatusSelectOptions('admin-certification', true)
        }
    },
    methods: {
        ...mapActions('adminCertification', ['fetchList', 'approve', 'reject']),
        ...mapActions('login', ['fetchConfig']),

        async fetchListHandler(params) {
            const filters = { ...params.filters }

            if (Object.keys(filters).length === 0 && !filters.status) {
                filters.status = this.statusFilterValue
            } else if (filters.status) {
                this.statusFilterValue = filters.status
            } else if (this.statusFilterValue && this.statusFilterValue !== CERTIFICATION_STATUS.ALL) {
                filters.status = this.statusFilterValue
            }

            if (filters.status === CERTIFICATION_STATUS.ALL) {
                delete filters.status
            }

            await this.fetchList({
                ...params,
                filters
            })
        },

        getStatusLabel(status) {
            return CERTIFICATION_STATUS_LABELS[status] || status
        },

        getStatusColor(status) {
            return CERTIFICATION_STATUS_COLORS[status] || 'light'
        },

        getStatusIcon(status) {
            return CERTIFICATION_STATUS_ICONS[status] || 'cipInfo'
        },

        onStatusFilterChange() {
            const filters = this.statusFilterValue === CERTIFICATION_STATUS.ALL
                ? {}
                : { status: this.statusFilterValue }

            if (this.$refs.ajaxTable) {
                this.$refs.ajaxTable.filters = filters
                this.$refs.ajaxTable.pagination.page = 1
                this.$refs.ajaxTable.fetchList()
            }
        },

        async handleApprove(item) {
            if (!confirm(this.$pgettext('admin-certification', 'Are you sure you want to approve this certification?'))) {
                return
            }

            try {
                await this.approve(item.id)
                await this.$refs.ajaxTable?.fetchList()
                this.notifySuccess(this.$pgettext('admin-certification', 'Certification approved successfully'))
            } catch (error) {
                this.notifyError(this.$pgettext('admin-certification', 'Error approving certification'))
            }
        },

        openRejectModal(item) {
            this.rejectingItem = item
            this.rejectReason = ''
            this.showRejectModal = true
        },

        async handleReject() {
            if (!this.rejectingItem || !this.rejectReason.trim()) return

            try {
                await this.reject({
                    id: this.rejectingItem.id,
                    rejectedReason: this.rejectReason.trim()
                })
                this.showRejectModal = false
                await this.$refs.ajaxTable?.fetchList()
                this.notifySuccess(this.$pgettext('admin-certification', 'Certification rejected'))
            } catch (error) {
                this.notifyError(this.$pgettext('admin-certification', 'Error rejecting certification'))
            }
        }
    },
    async mounted() {
        await this.fetchConfig({ type: 'admin_certifications' })
    }
}
</script>
